import { useMemo } from 'react';

import {
  calculateSigmoidParameters,
  createLinearFunctions,
  createPositionFunction,
  createValueFunction,
  EPSILON,
  validateAndNormalizeParameters,
  validateRanges,
} from './SkewedScaleUtils';

export type SkewedScaleOptions = {
  minPosition?: number;
  maxPosition?: number;
  minValue: number;
  maxValue: number;
  steepness?: number;
  bias?: number;
};

export type SkewedScale = {
  value: (position: number) => number;
  position: (value: number) => number;
};

const DEFAULT_STEEPNESS = 5;
const DEFAULT_BIAS = 0.5;

export const useSkewedScale = (options: SkewedScaleOptions): SkewedScale => {
  const {
    minPosition = 0,
    maxPosition = 100,
    minValue,
    maxValue,
    steepness: rawSteepness = DEFAULT_STEEPNESS,
    bias: rawBias = DEFAULT_BIAS,
  } = options;

  return useMemo(() => {
    // First, check if our range values make sense
    if (validateRanges(minValue, maxValue, minPosition, maxPosition)) {
      console.error(
        'useSkewedScale: Invalid ranges (min >= max). Returning linear identity functions.',
      );
      return createLinearFunctions(minPosition, maxPosition, minValue, maxValue);
    }

    // Make sure our steepness and bias values are reasonable
    const { steepness, bias } = validateAndNormalizeParameters(rawSteepness, rawBias);

    // If the curve is basically flat, just use a straight line
    if (steepness < EPSILON) {
      return createLinearFunctions(minPosition, maxPosition, minValue, maxValue);
    }

    // Setup the curve parameters based on our steepness and bias settings
    const { effectiveBias, sigmoidOutputMin, sigmoidOutputRange } = calculateSigmoidParameters(
      steepness,
      bias,
    );

    // If our curve is too flat to be useful, fall back to a straight line
    if (sigmoidOutputRange < EPSILON) {
      console.warn(
        'useSkewedScale: Sigmoid output range negligible, falling back to linear. Check steepness/bias.',
      );
      return createLinearFunctions(minPosition, maxPosition, minValue, maxValue);
    }

    // Create the value and position transformation functions
    const value = createValueFunction(
      minPosition,
      maxPosition,
      minValue,
      maxValue,
      steepness,
      effectiveBias,
      sigmoidOutputMin,
      sigmoidOutputRange,
    );

    const position = createPositionFunction(
      minPosition,
      maxPosition,
      minValue,
      maxValue,
      steepness,
      effectiveBias,
      sigmoidOutputMin,
      sigmoidOutputRange,
    );

    return { value, position };
  }, [minPosition, maxPosition, minValue, maxValue, rawSteepness, rawBias]);
};
